// F1 Race Detail Page
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useF1Championship } from '../../hooks/useF1Championship';
import F1PageTemplate from '../../components/f1/F1PageTemplate';
import { TableChart } from '../../components/charts';
import LoadingSpinner from '../../components/LoadingSpinner';

const F1RaceDetailPage = () => {
  const { raceId } = useParams();
  const { data, loading, error } = useF1Championship();

  if (loading) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <LoadingSpinner message="Loading Race Data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold text-red-600 mb-4">Error Loading Race</div>
          <div className="theme-secondary">{error}</div>
          <Link to="/f1/races" className="mt-4 inline-block px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            Back to Races
          </Link>
        </div>
      </div>
    );
  }

  // Find specific race
  const race = data?.races?.find(r => r.raceId == raceId);
  
  if (!race) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold theme-primary mb-4">Race Not Found</div>
          <div className="theme-secondary mb-4">Race with ID {raceId} could not be found.</div>
          <Link to="/f1/races" className="inline-block px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            Back to Races
          </Link>
        </div>
      </div>
    );
  }

  // Find circuit information
  const circuit = data?.circuits?.find(c => c.circuitId == race.circuitId);

  // Get all race results for this specific race
  const raceResults = data?.results?.filter(r => r.raceId == raceId) || [];
  
  // Get qualifying results for this specific race
  const qualifyingResults = data?.qualifying?.filter(q => q.raceId == raceId) || [];
  
  // Create driver name map
  const driverNameMap = {};
  data?.drivers?.forEach(driver => {
    driverNameMap[driver.driverId] = `${driver.forename} ${driver.surname}`;
  });

  // Create constructor name map
  const constructorNameMap = {};
  data?.constructors?.forEach(constructor => {
    constructorNameMap[constructor.constructorId] = constructor.name;
  });

  // Create status code map
  const statusMap = {};
  data?.status?.forEach(status => {
    statusMap[status.statusId] = status.status;
  });

  // Transform qualifying results for table
  const transformedQualifying = qualifyingResults
    .sort((a, b) => a.position - b.position)
    .map(q => {
      // Find corresponding race result to get final position and race data
      const raceResult = raceResults.find(r => r.driverId === q.driverId);
      const finalPosition = raceResult?.positionText || raceResult?.position || '-';
      
      const replaceNull = (value) => {
        if (value === null || value === undefined || value === '') {
          return '-';
        }
        if (typeof value === 'string' && (value === '\\N' || value === '\N')) {
          return '-';
        }
        return value;
      };
      
      return {
        position: q.position,
        driver: (
          <Link 
            to={`/f1/drivers/${q.driverId}`}
            className="theme-primary hover:theme-accent hover:underline font-medium"
          >
            {driverNameMap[q.driverId] || `Driver ${q.driverId}`}
          </Link>
        ),
        constructor: constructorNameMap[q.constructorId] || `Team ${q.constructorId}`,
        number: q.number,
        q1: replaceNull(q.q1),
        q2: replaceNull(q.q2),
        q3: replaceNull(q.q3),
        laps: raceResult?.laps || '-',
        points: raceResult?.points || '-',
        status: statusMap[raceResult?.statusId] || raceResult?.statusId || '-',
        gridPosition: q.position,
        finalPosition: replaceNull(finalPosition)
      };
    });

  const stats = [
    { value: race.year || 'Unknown', label: 'Year', color: 'theme-primary' },
    { value: race.round || 'Unknown', label: 'Round', color: 'theme-primary' },
    { value: race.date || 'Unknown', label: 'Date', color: 'theme-primary' },
    { value: circuit?.name || 'Unknown', label: 'Circuit', color: 'theme-primary' },
    { value: race.laps || 'Unknown', label: 'Laps', color: 'theme-primary' }
  ];

  return (
    <F1PageTemplate
      title={`${race.year} ${race.name}` || 'Race Details'}
      subtitle="Race Information and Results"
      summary={['Race details', 'Circuit information', 'Race results']}
      stats={stats}
    >
      <div className="space-y-8">
        {/* Season Overview */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">     
{/* Champion Card */}
            <div className="card">
              <h4 className="card-title">Race Champion</h4>
              <div className="space-y-2">
                <div className="text-center">
                  <div className="text-2xl font-bold theme-accent mb-1">
                    {race.topDrivers?.[0] ? (
                      <Link 
                        to={`/f1/drivers/${race.topDrivers[0].driverId}`}
                        className="theme-accent hover:underline"
                      >
                        {race.topDrivers[0].driverName}
                      </Link>
                    ) : 'Unknown'}
                  </div>
                  <div className="text-sm theme-secondary">Race Time</div>
                  <div className="text-xl font-semibold theme-primary">
                    {race.topDrivers?.[0]?.time || 'Unknown'}
                  </div>
                </div>
              </div>
            </div>
            {/* Champion Card */}
            <div className="card">
              <h4 className="card-title">Second Place</h4>
              <div className="space-y-2">
                <div className="text-center">
                  <div className="text-2xl font-bold theme-accent mb-1">
                    {race.topDrivers?.[1] ? (
                      <Link 
                        to={`/f1/drivers/${race.topDrivers[1].driverId}`}
                        className="theme-accent hover:underline"
                      >
                        {race.topDrivers[1].driverName}
                      </Link>
                    ) : 'Unknown'}
                  </div>
                  <div className="text-sm theme-secondary">Race Time</div>
                  <div className="text-xl font-semibold theme-primary">
                    {race.topDrivers?.[1]?.time || 'Unknown'}
                  </div>
                </div>
              </div>
            </div>
            {/* Champion Card */}
            <div className="card">
              <h4 className="card-title">Third Place</h4>
              <div className="space-y-2">
                <div className="text-center">
                  <div className="text-2xl font-bold theme-accent mb-1">
                    {race.topDrivers?.[2] ? (
                      <Link 
                        to={`/f1/drivers/${race.topDrivers[2].driverId}`}
                        className="theme-accent hover:underline"
                      >
                        {race.topDrivers[2].driverName}
                      </Link>
                    ) : 'Unknown'}
                  </div>
                  <div className="text-sm theme-secondary">Race Time</div>
                  <div className="text-xl font-semibold theme-primary">
                    {race.topDrivers?.[2]?.time || 'Unknown'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Circuit Information */}
        {circuit && (
          <div className="card">
            <h3 className="text-xl font-semibold theme-primary mb-4">Circuit Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <span className="font-medium theme-secondary">Circuit Name:</span>
                <span className="ml-2 theme-primary">{circuit.name}</span>
              </div>
              <div>
                <span className="font-medium theme-secondary">Location:</span>
                <span className="ml-2 theme-primary">{circuit.location || 'Unknown'}, {circuit.country || 'Unknown'}</span>
              </div>

              <div>
                <span className="font-medium theme-secondary">Track Length:</span>
                <span className="ml-2 theme-primary">{circuit.length ? `${circuit.length}km` : 'Unknown'}</span>
              </div>
              <div>
                <span className="font-medium theme-secondary">Circuit URL:</span>
                <span className="ml-2 theme-primary">
                  {circuit.url ? (
                    <a href={circuit.url} target="_blank" rel="noopener noreferrer" className="theme-accent hover:underline">
                      View Circuit Info
                    </a>
                  ) : 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Qualifying Results Table */}
        {transformedQualifying && transformedQualifying.length > 0 && (
          <TableChart 
            data={transformedQualifying}
            title="Race Results"
            subtitle="Grid positions and qualifying lap times"
            config={{
              columns: [
                { key: 'driver', label: 'Driver' },
                { key: 'constructor', label: 'Constructor' },
                { key: 'number', label: 'Number' },
                { key: 'q1', label: 'Q1' },
                { key: 'q2', label: 'Q2' },
                { key: 'q3', label: 'Q3' },
                { key: 'laps', label: 'Laps' },
                { key: 'points', label: 'Points' },
                { key: 'status', label: 'Status' },
                { key: 'gridPosition', label: 'Grid Position' },
                { key: 'finalPosition', label: 'Final Position' }
              ],
              sortable: true,
              searchable: true,
              sortBy: 'finalPosition',
              sortDirection: 'asc',
              rowsPerPage: 20
            }}
          />
        )}
      </div>
    </F1PageTemplate>
  );
};

export default F1RaceDetailPage;
